import api from "../../services/api";
import { Eventbus } from "../../utils";
import { events } from "../../utils/constants";
import logger from "../../utils/logger";

const createChildMatchService = (id) => {

    // Private data cache
    let data = [];
    let isListenerRegistered = false;
    let isLoading = false; // Add loading flag to prevent duplicate requests
    let currentCentreId = id;    function getData() {
        return [...data]; // Return a copy to prevent external mutation
    } 
    
    function mergeChildMatch(updatedRecord) {
        logger.debug('Merging child match record:', updatedRecord);
        try {
            if (!updatedRecord || !updatedRecord.id) {
                logger.warn('Invalid child match record received for merge:', updatedRecord);
                return;
            }

            const existingRecordIndex = data.findIndex(record => record.id === updatedRecord.id);

            if (existingRecordIndex !== -1) {
                const existingRecord = data[existingRecordIndex];
                
                // Check updatedAt timestamp to prevent old records from overwriting newer ones
                if (updatedRecord.updatedAt && existingRecord.updatedAt) {
                    const updatedTime = new Date(updatedRecord.updatedAt);
                    const existingTime = new Date(existingRecord.updatedAt);
                    
                    if (updatedTime < existingTime) {
                        logger.debug('Ignoring older child match record:', updatedRecord.id, {
                            incoming: updatedRecord.updatedAt,
                            existing: existingRecord.updatedAt
                        });
                        return;
                    }
                }
                
                // Update existing record in place to maintain array references
                data[existingRecordIndex] = { ...existingRecord, ...updatedRecord };
                logger.debug('Updated existing child match record:', updatedRecord.id);
            } else {
                // Add new record if it doesn't exist
                data.push(updatedRecord);
                logger.debug('Added new child match record:', updatedRecord.id);
            }

            Eventbus.emit(events.CHILD_MATCHES_UPDATED, [...data]); // Emit updated data
        } catch (error) {
            logger.error('Error merging child match record:', error, updatedRecord);
        }
    }
    
    function updateChildMatch(id, childMatch) {
        try {
            if (!id || !childMatch) {
                logger.warn('Invalid child match data received for update:', { id, childMatch });
                return Promise.reject(new Error('Invalid child match data'));
            }
            
            let existingChildMatch = data.find(match => match.id === id);
            let record;
            
            if (existingChildMatch) {
                // Update existing record
                record = { ...existingChildMatch, ...childMatch };
                logger.debug('Updating existing child match record:', { id, childMatch });
            } else {
                // Create new record if it doesn't exist
                record = { id, ...childMatch };
                logger.debug('Creating new child match record:', { id, childMatch });
            }
            
            return api.updateChildMatch(id, record)
                .then((response) => {
                    if (response.code === 'ERR_CANCELED') {
                        logger.warn('Child match update cancelled:', { id, record });
                        return response; // Exit if the request was cancelled
                    }
                    
                    // Update local data using merge function
                    // mergeChildMatch(response);
                    logger.debug('Child match updated successfully:', response);
                    return response;
                })
                .catch((error) => {
                    logger.error('Error updating child match:', error, { id, childMatch });
                    throw error;
                });

        } catch (error) {
            logger.error('Error updating child match record:', error, { id, childMatch });
            return Promise.reject(error);
        }
    }

   
    
    function loadData(forceRefresh = false) {

        // Prevent duplicate requests
        if (isLoading && !forceRefresh) {
            return Promise.resolve([...data]);
        }

        // If data exists and we're not forcing a refresh, return the cached data
        if (data.length > 0 && !forceRefresh) {
            isLoading = false; // Ensure loading state is reset when returning cached data
            Eventbus.emit(events.CHILD_MATCHES_LOADED, [...data]);
            return Promise.resolve([...data]);
        }

        if (!currentCentreId) {
            logger.warn('Centre ID is required to load child matches');
            return Promise.reject(new Error('Centre ID is required'));
        }

        isLoading = true;

        // Otherwise fetch fresh data from the API
        return api.getChildMatchByCentre(currentCentreId)
            .then((response) => {
                if (response.code === 'ERR_CANCELED') {
                    return []; // Return empty array on cancellation
                }
                data = response || [];
                logger.debug('Child matches loaded for centre:', currentCentreId, data);
                Eventbus.emit(events.CHILD_MATCHES_LOADED, response);                if (!isListenerRegistered) {
                    Eventbus.on(events.SIGNALR.WINZ_CHILD_MATCH_DATA_CHANGES, mergeChildMatch);
                    isListenerRegistered = true;
                    logger.debug('SignalR child match data changes listener registered');
                }
                return [...data];
            })
            .catch((error) => {
                logger.error('Error fetching child matches for centre:', error);
                throw error;
            })
            .finally(() => {
                isLoading = false;
            });
    }
    
    function init() {
       
        return loadData(true);
    }    
    
    function clear() {
        if (isListenerRegistered) {
            Eventbus.off(events.SIGNALR.WINZ_CHILD_MATCH_DATA_CHANGES, mergeChildMatch);
            isListenerRegistered = false;
            logger.debug('SignalR child match data changes listener removed');
        }
        data = [];
        currentCentreId = null;
    }
    
    function deleteChildMatch(id) {
        return api.deleteChildMatch(id)
            .then((response) => {
                // Remove from local cache immediately
                const index = data.findIndex(record => record.id === id);
                if (index !== -1) {
                    data.splice(index, 1);
                    Eventbus.emit(events.CHILD_MATCHES_UPDATED, [...data]);
                }
                return response;
            })
            .catch((error) => {
                logger.error('Error deleting child match:', error);
                throw error;
            });
    }    
    function getLoadingState() {
        return isLoading;
    }

    function createChildMatch(childMatchData) {
        try {
            if (!childMatchData) {
                logger.warn('Invalid child match data for creation:', childMatchData);
                return Promise.reject(new Error('Invalid child match data'));
            }

            logger.debug('Creating child match:', childMatchData);
            
            return api.createChildMatch(childMatchData)
                .then((response) => {
                    if (response.code === 'ERR_CANCELED') {
                        logger.warn('Child match creation cancelled:', childMatchData);
                        return response;
                    }
                    
                    // Add the new child match to local data
                    // data.push(childMatchData);
                    // Eventbus.emit(events.CHILD_MATCHES_UPDATED, [...data]);
                    
                    logger.debug('Child match created successfully:', response);
                    return response;
                })
                .catch((error) => {
                    logger.error('Error creating child match:', error, childMatchData);
                    throw error;
                });

        } catch (error) {
            logger.error('Error creating child match:', error, childMatchData);
            return Promise.reject(error);
        }
    }

    return {
        getData,
        loadData,
        updateChildMatch,
        createChildMatch,
        init,
        clear,
        deleteChildMatch,
        getLoadingState,
    };
};

export default createChildMatchService;
